import React, { useState, useRef, useEffect, useMemo } from 'react';
import { collection, addDoc, getDocs, query, where, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Loader2, MapPin, User, Phone, ShieldCheck, CreditCard, History, HeartHandshake, MessageSquare, ShoppingBag, ShieldAlert, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import { Button } from './Button';
import { db } from '../firebase';
import { RAZORPAY_KEY_ID, SUCCESS_SOUND_URL, LOGO_URL } from '../constants';
import { ShippingAddress, Coupon } from '../types';

// Import your custom Lottie animation
import successAnimation from '../src/assets/success-animation.json';

declare var Razorpay: any;

export const Checkout: React.FC = () => {
  const { items, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const { user, setIsLoginOpen, updateUserProfile } = useAuth(); 
  const { showNotification } = useNotification();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [sameAsProfile, setSameAsProfile] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponError, setCouponError] = useState('');
  const [isVerifyingCoupon, setIsVerifyingCoupon] = useState(false);

  const availability = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    return day >= 1 && day <= 6 && hour >= 10 && hour < 18;
  }, []);

  const [shipping, setShipping] = useState<ShippingAddress>({
    fullName: user?.fullName || user?.name || '',
    address: user?.shippingAddress?.address || '',
    pincode: user?.shippingAddress?.pincode || '',
    state: user?.shippingAddress?.state || '',
    district: user?.shippingAddress?.district || '',
    mobile: user?.mobile || user?.shippingAddress?.mobile || ''
  });

  const [alternativeMobile, setAlternativeMobile] = useState('');

  const [billing, setBilling] = useState<ShippingAddress>({
    fullName: user?.name || '',
    address: '',
    pincode: '',
    state: '',
    district: '',
    mobile: user?.mobile || ''
  });

  useEffect(() => {
    if (user?.shippingAddress) {
      setShipping(user.shippingAddress);
      setIsEditing(false);
    } else {
      setIsEditing(true);
      if (user) {
        setShipping(prev => ({
          ...prev,
          fullName: user.fullName || user.name || prev.fullName,
          mobile: user.mobile || prev.mobile
        }));
      }
    }

    if (user) {
      setBilling((prev) => ({
        ...prev,
        fullName: user.fullName || user.name || prev.fullName,
        mobile: user.mobile || prev.mobile,
      }));
    }
  }, [user]);

  const successAudio = useRef<HTMLAudioElement | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBilling({ ...billing, [e.target.name]: e.target.value });
  };

  const playSuccessSound = () => {
    if (!successAudio.current) {
      successAudio.current = new Audio(SUCCESS_SOUND_URL);
    }
    // 1.0 is the absolute maximum allowed by web browsers (100% volume)
    successAudio.current.volume = 1.0; 
    try {
      successAudio.current.currentTime = 0;
    } catch {
      // ignore
    }
    successAudio.current.play().catch(e => console.log("Sound play prevented", e));
  };

  const validateShipping = () => {
    return shipping.fullName && 
           shipping.address && 
           shipping.pincode && 
           shipping.state && 
           shipping.district && 
           shipping.mobile && 
           /^\d{10}$/.test(shipping.mobile) &&
           (!alternativeMobile || /^\d{10}$/.test(alternativeMobile));
  };

  const validateBilling = () => {
    if (billingSameAsShipping) return true;
    return billing.fullName && billing.address && billing.pincode && billing.state && billing.district && billing.mobile;
  };

  const verifyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsVerifyingCoupon(true);
    setCouponError('');

    try {
      const couponsQuery = query(collection(db, 'coupons'), where('code', '==', couponCode.toUpperCase().trim()));
      const snapshot = await getDocs(couponsQuery);
      
      if (snapshot.empty) {
        setCouponError('Invalid coupon code');
        return;
      }

      const coupon = snapshot.docs[0].data() as Coupon;
      
      if (!coupon.isActive) {
        setCouponError('Coupon is not active');
        return;
      }

      if (coupon.minOrderValue && cartTotal < coupon.minOrderValue) {
        setCouponError(`Minimum order value of ₹${coupon.minOrderValue} required`);
        return;
      }

      setAppliedCoupon({ ...coupon, id: snapshot.docs[0].id });
      setCouponCode('');
      showNotification('Coupon applied successfully!', 'success');
    } catch (error) {
      console.error('Coupon verification error:', error);
      setCouponError('Failed to verify coupon');
    } finally {
      setIsVerifyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    
    if (appliedCoupon.discountType === 'percentage') {
      return (cartTotal * appliedCoupon.discountValue) / 100;
    } else {
      return appliedCoupon.discountValue;
    }
  };

  const finalTotal = cartTotal - calculateDiscount();

  const verifyAndSavePayment = async (razorpayResponse: any) => {
    try {
      showNotification("Verifying payment security...", "success");
      
      const verifyRes = await fetch('/api/verifyPayment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature
        })
      });
      
      const verifyData = await verifyRes.json();

      if (!verifyData.success) {
        showNotification("Security verification failed. Payment rejected.", "error");
        setIsProcessing(false);
        return; 
      }

      await handlePaymentSuccess(razorpayResponse.razorpay_payment_id);
      
    } catch (error) {
      console.error("Payment verification error:", error);
      showNotification("Verification Error. Contact support if amount was deducted.", "error");
      setIsProcessing(false);
    }
  };

  const initiatePayment = async () => {
    if (!user) {
      setIsLoginOpen(true);
      return;
    }

    if (!RAZORPAY_KEY_ID) {
      showNotification('Razorpay key is missing. Set VITE_RAZORPAY_KEY_ID in Vercel env.', 'error');
      return;
    }

    if (!validateShipping()) {
      showNotification("Details incomplete.", "error");
      setIsEditing(true);
      return;
    }

    if (!validateBilling()) {
      showNotification("Billing details incomplete.", "error");
      setIsEditing(true);
      return;
    }

    setIsProcessing(true);
    
    try {
      const orderResponse = await fetch('/api/createOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: finalTotal * 100,
          currency: 'INR',
          receipt: `receipt_${Date.now()}`,
          notes: {
            customer: user?.mobile,
            total_amount: finalTotal,
            customer_name: shipping.fullName
          }
        })
      });

      const orderData = await orderResponse.json();
      
      if (!orderData.success) {
        console.error('Order creation failed:', orderData);
        showNotification(`Order failed: ${orderData.error || 'Unknown error'}`, "error");
        setIsProcessing(false);
        return;
      }

      const options = {
        key: orderData.key_id,
        amount: finalTotal * 100,
        currency: "INR",
        name: "Devine stone",
        description: "Premium Gemstone Acquisition",
        image: LOGO_URL,
        order_id: orderData.order.id,
        handler: function (response: any) {
          verifyAndSavePayment(response);
        },
        prefill: {
          name: shipping.fullName,
          email: user?.email || undefined,
          contact: shipping.mobile,
        },
        notes: {
          address: shipping.address,
          alternativeMobile: alternativeMobile,
        },
        theme: {
          color: "#f59e0b",
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
          }
        }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', function (response: any) {
        showNotification(`Failed: ${response.error.description}`, "error");
        setIsProcessing(false);
      });
      rzp.open();
    } catch (error) {
      showNotification("Gateway Error.", "error");
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paymentId: string) => {
    setIsProcessing(true);
    try {
      const finalBilling = billingSameAsShipping ? shipping : billing;
      
      const orderItems = items.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        images: item.images || [], 
        price: item.currentPrice,
        unitPrice: (typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice)) ? item.unitPrice : item.currentPrice,
        selectedVariants: item.selectedVariants || [],
        total: ((typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice)) ? item.unitPrice : item.currentPrice) * item.quantity
      }));

      const docRef = await addDoc(collection(db, "orders"), {
        userId: user?.mobile,
        userEmail: user?.email || '',
        userName: user?.fullName || user?.name,
        shipping: shipping,
        billing: finalBilling,
        items: orderItems,
        totalAmount: finalTotal,
        discountAmount: calculateDiscount(),
        couponCode: appliedCoupon?.code || null,
        currency: 'INR',
        status: 'Just Ordered',
        createdAt: new Date().toISOString(),
        paymentMethod: 'Razorpay',
        paymentId: paymentId,
        alternativeMobile: alternativeMobile || null,
      });

      setCompletedOrderId(docRef.id);

      for (const item of items) {
        try {
          const productRef = doc(db, 'products', item.id);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const currentQty = productSnap.data().quantityLeft || 0;
            if (currentQty > 0) {
              await updateDoc(productRef, {
                quantityLeft: currentQty - item.quantity
              });
            }
          }
        } catch (qtyErr) {
          console.error("Error updating quantity:", qtyErr);
        }
      }
      
      playSuccessSound();
      showNotification("Confirmed!", "success");
      setIsSuccess(true);
      clearCart();
    } catch (error) {
      console.error(error);
      showNotification("Database Error.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  // 💎 PREMIUM SUCCESS SCREEN (Minimalist & Clutter-Free) 💎
  if (isSuccess) {
    return (
      <div className="container mx-auto px-4 flex flex-col items-center justify-center min-h-[85vh] animate-[fadeIn_0.5s_ease-out]">
        
        {/* Lottie Success Animation (loop={false} ensures it plays once and stops) */}
        <div className="w-64 h-64 sm:w-[350px] sm:h-[350px] flex items-center justify-center mb-6">
          <Lottie 
            animationData={successAnimation} 
            loop={false} 
          />
        </div>

        {/* Minimalist Success Text */}
        <h2 className="text-2xl md:text-3xl font-serif font-black text-stone-900 uppercase tracking-widest text-center mb-8">
          Acquisition Secured
        </h2>

        {/* Action Button */}
        <Button 
          onClick={() => navigate('/orders')} 
          className="px-10 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl bg-stone-900 text-white hover:bg-stone-800 transition-all"
        >
          View Your Collection
        </Button>
        
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-6 py-20 text-center flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 border border-orange-100">
           <CreditCard size={24} className="text-stone-300" />
        </div>
        <h2 className="text-xl font-serif font-medium text-stone-900 mb-4 uppercase tracking-widest">Registry Empty</h2>
        <Button onClick={() => navigate('/')} className="rounded-2xl font-medium">Return to Store</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="text-lg md:text-2xl font-serif font-medium text-stone-900 uppercase tracking-tighter">Checkout</h2>
          <p className="text-xs md:text-sm font-medium uppercase tracking-[0.2em] text-stone-400 mt-1">Premium Acquisition Protocol</p>
        </div>
        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-2xl border border-orange-100 shadow-sm">
           <ShieldCheck className="text-green-500" size={14} />
           <span className="text-[10px] font-medium uppercase tracking-widest text-stone-600">Razorpay Protected</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          <div className="bg-white p-5 rounded-3xl border border-orange-100 shadow-sm relative overflow-hidden group">
             <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100"><History size={16} /></div>
                   <div><p className="text-orange-700 font-medium uppercase text-[10px] tracking-widest">Legacy</p><p className="text-stone-900 font-medium text-xs uppercase">10+ Years Experience</p></div>
                </div>
                <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100"><HeartHandshake size={16} /></div>
                   <div><p className="text-orange-700 font-medium uppercase text-[10px] tracking-widest">Quality</p><p className="text-stone-900 font-medium text-xs uppercase">Premium Finds</p></div>
                </div>
                <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100"><ShieldCheck size={16} /></div>
                   <div><p className="text-orange-700 font-medium uppercase text-[10px] tracking-widest">Assurance</p><p className="text-stone-900 font-medium text-xs uppercase">Refund Policy</p></div>
                </div>
             </div>
          </div>

          <div className="bg-white p-5 md:p-6 rounded-3xl shadow-sm border border-orange-100">
            <div className="flex justify-between items-center mb-6 border-b border-orange-100 pb-3">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-stone-900 flex items-center gap-2">
                <MapPin size={14} className="text-orange-600" /> 1. Shipping Destination
              </h2>
              {!isEditing && (
                <button onClick={() => setIsEditing(true)} className="text-[10px] font-medium uppercase text-stone-400 hover:text-orange-600 transition-colors">
                  UPDATE
                </button>
              )}
            </div>

            {/* Same as Profile Checkbox */}
            <div className="mb-4 p-3 bg-orange-50/40 rounded-xl border border-orange-100">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsProfile}
                  onChange={(e) => {
                    setSameAsProfile(e.target.checked);
                    if (e.target.checked && user?.shippingAddress) {
                      setShipping({
                        fullName: user?.fullName || user?.name || '',
                        address: user?.shippingAddress?.address || '',
                        pincode: user?.shippingAddress?.pincode || '',
                        state: user?.shippingAddress?.state || '',
                        district: user?.shippingAddress?.district || '',
                        mobile: user?.mobile || user?.shippingAddress?.mobile || ''
                      });
                    }
                  }}
                  className="w-4 h-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                />
                <span className="text-xs font-medium text-stone-700">Same as profile details</span>
              </label>
            </div>

            {isEditing && !sameAsProfile ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="shipping-name">Name</label><input id="shipping-name" name="fullName" value={shipping.fullName} onChange={handleInputChange} type="text" placeholder="Receiver" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium text-stone-900 outline-none" /></div>
                <div className="space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="shipping-mobile">Main Mobile</label><input id="shipping-mobile" name="mobile" value={shipping.mobile} onChange={handleInputChange} type="tel" placeholder="10-digit mobile" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-mono font-medium text-stone-900 outline-none" maxLength={10} /></div>
                <div className="space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="shipping-alt-mobile">Alternative Mobile</label><input id="shipping-alt-mobile" name="alternativeMobile" value={alternativeMobile} onChange={(e) => setAlternativeMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} type="tel" placeholder="10-digit alternative" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-mono font-medium text-stone-900 outline-none" maxLength={10} /></div>
                <div className="md:col-span-2 space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="shipping-email">Email (optional)</label><input id="shipping-email" value={user?.email || ''} disabled type="email" placeholder="you@example.com" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-stone-100 text-base font-medium text-stone-600 outline-none" /></div>
                <div className="md:col-span-2 space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="shipping-address">Address</label><input id="shipping-address" name="address" value={shipping.address} onChange={handleInputChange} type="text" placeholder="Full Destination" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium text-stone-900 outline-none" /></div>
                <div className="grid grid-cols-3 gap-2 md:col-span-2">
                  <input id="shipping-district" name="district" value={shipping.district} onChange={handleInputChange} placeholder="City" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium outline-none" />
                  <input id="shipping-state" name="state" value={shipping.state} onChange={handleInputChange} placeholder="State" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium outline-none" />
                  <input id="shipping-pincode" name="pincode" value={shipping.pincode} onChange={handleInputChange} placeholder="PIN" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-mono font-medium outline-none" />
                </div>
              </div>
            ) : (
              <div className="p-4 bg-orange-50/40 rounded-3xl border border-orange-100 space-y-2">
                 <p className="font-medium uppercase tracking-wider text-stone-900 text-[10px] flex items-center gap-2"><User size={12} className="text-orange-600" /> {shipping.fullName}</p>
                 <p className="font-mono font-medium text-stone-900 text-[10px] flex items-center gap-2"><Phone size={12} className="text-orange-600" /> {shipping.mobile}</p>
                 {alternativeMobile && (
                   <p className="font-mono font-medium text-stone-900 text-[10px] flex items-center gap-2"><Phone size={12} className="text-orange-600" /> Alt: {alternativeMobile}</p>
                 )}
                 <p className="text-stone-700 leading-relaxed text-[10px] font-medium uppercase flex items-start gap-2"><MapPin size={12} className="text-orange-600 mt-0.5" /> {shipping.address}, {shipping.district}, {shipping.state} - {shipping.pincode}</p>
              </div>
            )}
          </div>

          <div className="bg-white p-5 md:p-6 rounded-3xl shadow-sm border border-orange-100">
            <div className="flex justify-between items-center mb-6 border-b border-orange-100 pb-3">
              <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-stone-900 flex items-center gap-2">
                <MapPin size={14} className="text-orange-600" /> 2. Billing Destination
              </h2>
            </div>

            <label className="flex items-center gap-2 text-[10px] font-medium text-stone-700">
              <input
                type="checkbox"
                checked={billingSameAsShipping}
                onChange={(e) => setBillingSameAsShipping(e.target.checked)}
              />
              Billing address is same as shipping
            </label>

            {!billingSameAsShipping && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="billing-name">Name</label><input id="billing-name" name="fullName" value={billing.fullName} onChange={handleBillingChange} type="text" placeholder="Billing Name" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium text-stone-900 outline-none" /></div>
                <div className="space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="billing-mobile">Contact</label><input id="billing-mobile" name="mobile" value={billing.mobile} onChange={handleBillingChange} type="tel" placeholder="Mobile" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-mono font-medium text-stone-900 outline-none" /></div>
                <div className="md:col-span-2 space-y-1"><label className="text-xs font-medium uppercase tracking-widest text-stone-400 ml-1" htmlFor="billing-address">Address</label><input id="billing-address" name="address" value={billing.address} onChange={handleBillingChange} type="text" placeholder="Billing Address" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium text-stone-900 outline-none" /></div>
                <div className="grid grid-cols-3 gap-2 md:col-span-2">
                  <input id="billing-district" name="district" value={billing.district} onChange={handleBillingChange} placeholder="City" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium outline-none" />
                  <input id="billing-state" name="state" value={billing.state} onChange={handleBillingChange} placeholder="State" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-medium outline-none" />
                  <input id="billing-pincode" name="pincode" value={billing.pincode} onChange={handleBillingChange} placeholder="PIN" className="w-full px-4 py-2.5 border border-orange-200 rounded-xl bg-white text-base font-mono font-medium outline-none" />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-5 md:p-6 rounded-3xl shadow-sm border border-orange-100">
             <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-stone-900 mb-5 border-b border-orange-100 pb-3 flex items-center gap-2">
               <CreditCard size={14} className="text-orange-600" /> 3. Secure Settlement
             </h2>
             <div className="flex items-center gap-4 p-4 border border-orange-200 bg-orange-50/50 rounded-3xl">
               <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center border border-orange-200"><ShieldCheck className="text-orange-600" size={16} /></div>
               <div>
                 <p className="font-medium text-stone-900 text-[10px] uppercase tracking-widest">Razorpay Online Checkout</p>
                 <p className="text-[9px] text-stone-500 font-medium uppercase tracking-tight">Encrypted Digital Settlement Only</p>
               </div>
             </div>
          </div>
        </div>
        
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-3xl shadow-2xl border border-orange-100 sticky top-24">
            <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-stone-900 mb-4 flex items-center gap-2">
               <ShoppingBag size={12} className="text-orange-600" /> Cart Log
            </h3>
            <div className="space-y-2 mb-4">
               {items.map(item => (
                 <div key={`${item.id}-${JSON.stringify(item.selectedVariants || [])}`} className="flex justify-between items-center text-[10px] font-medium uppercase">
                    <span className="text-stone-500 truncate max-w-[120px]">{item.name} <span className="text-orange-600">x{item.quantity}</span></span>
                    <span className="text-stone-900 whitespace-nowrap font-medium">₹{(((typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice)) ? item.unitPrice : item.currentPrice) * item.quantity).toLocaleString('en-IN')}</span>
                 </div>
               ))}
            </div>
            <div className="border-t border-orange-100 pt-4 mb-5 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-stone-400 uppercase tracking-widest">Total Value</span>
                <span className="text-lg font-medium text-stone-900 tracking-tighter">₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
              
              {appliedCoupon && (
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-medium text-green-600 uppercase tracking-widest flex items-center gap-1">
                    <Tag size={10} /> Discount ({appliedCoupon.discountType === 'percentage' ? `${appliedCoupon.discountValue}%` : `₹${appliedCoupon.discountValue}`})
                  </span>
                  <span className="text-sm font-medium text-green-600">-₹{calculateDiscount().toLocaleString('en-IN')}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center pt-2 border-t border-orange-100">
                <span className="text-xs font-medium text-stone-900 uppercase tracking-widest">Final Amount</span>
                <span className="text-xl font-medium text-orange-700 tracking-tighter">₹{finalTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>
            
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <input
                  id="coupon-code"
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    setCouponError('');
                  }}
                  placeholder="CODE"
                  className="flex-1 px-3 py-2 text-sm font-medium rounded-xl border border-orange-200 outline-none focus:border-orange-500"
                  disabled={isVerifyingCoupon || !!appliedCoupon}
                />
                {!appliedCoupon ? (
                  <Button 
                    onClick={verifyCoupon} 
                    disabled={isVerifyingCoupon || !couponCode.trim()}
                    size="sm"
                    className="rounded-xl px-4 font-medium"
                  >
                    {isVerifyingCoupon ? <Loader2 size={12} className="animate-spin" /> : 'Apply'}
                  </Button>
                ) : (
                  <Button
                    onClick={removeCoupon}
                    variant="outline"
                    size="sm"
                    className="rounded-xl px-4 font-medium border-red-200 text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                )}
              </div>
              {couponError && <p className="text-[10px] text-red-500 font-medium ml-1">{couponError}</p>}
            </div>

            <Button 
              onClick={initiatePayment} 
              disabled={isProcessing}
              className="w-full py-4 rounded-2xl text-xs font-medium uppercase tracking-widest shadow-lg shadow-orange-200"
            >
              {isProcessing ? (
                <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Encrypting...</span>
              ) : (
                <span className="flex items-center gap-2">Acquire Now</span>
              )}
            </Button>
            
            <div 
              onClick={() => navigate('/consult')}
              className={`mt-6 flex flex-col items-center gap-2 cursor-pointer group p-4 rounded-2xl border transition-all ${availability ? 'border-orange-300 bg-orange-50 ring-4 ring-orange-200/30' : 'border-dashed border-orange-200'}`}
            >
               <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${availability ? 'bg-green-500 animate-pulse' : 'bg-stone-400'}`}></div>
                  <div className="flex items-center gap-2 text-[8px] font-medium uppercase tracking-widest text-stone-900">
                    <MessageSquare size={10} className="text-orange-600" /> {user ? 'Problems? Consult with us' : 'Talk with us after signing in'}
                  </div>
               </div>
               <p className="text-[7px] text-stone-400 font-medium uppercase text-center leading-tight">Expert Curator Access {availability ? '(Live Now)' : '(Scheduled Only)'}</p>
            </div>
            
            <div className="mt-4 flex items-center justify-center gap-2 opacity-30">
               <ShieldAlert size={10} />
               <span className="text-[7px] font-medium uppercase tracking-widest">Encrypted Frequency</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
